// useBeneficiaries — TanStack Query hook for the current merchant's beneficiary list.
//
// Mirrors the useBankAccounts() pattern: selects status from useAuth and
// currentMerchantId from useUI; guards the query on both.
//
// Used by the UploadBatchModal (Plan 06-05) to pre-load beneficiaries on open
// so that validateRows can resolve beneficiary IDs without per-row network calls
// (RESEARCH Pattern 7: pre-load approach).
//
// staleTime is 60s (longer than payouts/batch hooks) because beneficiary
// registrations change infrequently compared to payout statuses. No polling —
// the list is fetched once per merchant per session and refreshed on stale.
import { useQuery } from "@tanstack/react-query";

import { listBeneficiaries } from "@/api/beneficiaries";
import { useAuth } from "@/store/auth";
import { useUI } from "@/store/ui";

/**
 * useBeneficiaries — loads the current merchant's beneficiaries for client-side
 * validation pre-load.
 *
 * queryKey: ["beneficiaries", currentMerchantId] — per-merchant scope so the
 * cache is correct when the user switches merchants (T-06-14 isolation).
 *
 * Guards:
 *   - enabled: false when unauthenticated or no merchant selected.
 */
export function useBeneficiaries() {
  const status = useAuth((s) => s.status);
  const currentMerchantId = useUI((s) => s.currentMerchantId);

  return useQuery({
    queryKey: ["beneficiaries", currentMerchantId],
    queryFn: () => listBeneficiaries(currentMerchantId!),
    enabled: status === "authenticated" && currentMerchantId != null,
    staleTime: 60_000,
  });
}
